import { useState } from "react";
import { Accordion, Alert, Button, Card, Col, Form, FormGroup, Image, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DayOfTheWeekForm from "../../../components/forms/configuration/DayOfTheWeekForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import DayOfTheWeekService from "../../../services/dayOfTheWeek.service";
import { useEffect } from "react";
import BodyService from "../../../services/body.service";
import UseAlertConfirm from "../../../hooks/UseAlertConfirm";
import { useForm } from "react-hook-form";
import PaginationComponent from "../../../components/PaginationComponent";
import TableHeaderSortComponent from "../../../components/TableHeaderSortComponent";
import APP_CONF from "../../../scripts/constants";
import UserPermissionService from "../../../services/userPermission.service";


const DayOfTheWeek = () => {

    const { t } = useTranslation();

    const [translations, setTranslations] = useState([]);

    const [daysOfTheWeeks, setDaysOfTheWeeks] = useState([]);

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });

    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const { showModal } = UseAlertConfirm();

    const [filter, setFilter] = useState({
        active: ""
    })

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const [permissions, setPermissions] = useState({});

    const createDayOfTheWeek = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });
        try {
            await DayOfTheWeekService.create(data);
            getEmptyTranslations();
            setSuccess({ ...success, create: true });
            getDaysOfTheWeek();
        } catch (error) {
            console.error(error.response);
            setErrors({ ...errors, create: true });
        }
    }

    const deleteDayOfTheWeek = async (id) => {
        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await DayOfTheWeekService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getDaysOfTheWeek();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });
    }

    const getDaysOfTheWeek = async () => {
        try {
            const data = await DayOfTheWeekService.getAll(pagination, filter, sortColumn);
            setDaysOfTheWeeks(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch (response) {
            if (response?.data?.errorCode) {
                switch (response.data.errorCode) {
                    case 13002:
                        setErrors({ ...errors, email: true });
                        break;
                    default:
                        setErrors({ ...errors, create: true });
                }
            }
        }
    }

    const getEmptyTranslations = async () => {
        const data = await BodyService.getEmptyTranslations();
        setTranslations(data);
    };

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await getPermissions();
            await getEmptyTranslations();
        }

        loadData();
    }, []);

    useEffect(() => {
        getDaysOfTheWeek();
    }, [pagination.currentPage, filter, sortColumn]);

    const filterSearch = (data) => {
        let obj = data;
        if (obj.search === "") {
            obj = {
                active: data.active
            }
        }
        setFilter(obj);
    }

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('dayOfTheWeek.title')}</h1>
            </div>

            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('dayOfTheWeek.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('dayOfTheWeek.success.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('dayOfTheWeek.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('dayOfTheWeek.error.delete')}
            </Alert>
            {permissions.weekDays?.read && permissions.weekDays?.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('dayOfTheWeek.form.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <DayOfTheWeekForm onSubmit={createDayOfTheWeek} translations={translations} permissions={permissions} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
            : null }
            <hr />
            <Card className={"shadow mb-4"}>
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('dayOfTheWeek.table.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('dayOfTheWeek.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('dayOfTheWeek.filter.active.value1')}</option>
                                        <option value={true}>{t('dayOfTheWeek.filter.active.value2')}</option>
                                        <option value={false}>{t('dayOfTheWeek.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('dayOfTheWeek.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.weekDays?.read}>{t('dayOfTheWeek.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                            <tr>
                                <TableHeaderSortComponent scope={"col"} text={t('dayOfTheWeek.table.name')} column={"name"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('dayOfTheWeek.table.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                daysOfTheWeeks ? daysOfTheWeeks.map((dayOfTheWeek, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex flex-row flex-nowrap">
                                                {Object.values(dayOfTheWeek.dayOfTheWeek).map((translation, i) =>
                                                    <div key={i} className="me-2">
                                                        <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                        {i + 1 === Object.values(dayOfTheWeek.dayOfTheWeek).length ? translation.value : translation.value + ' | '}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>{dayOfTheWeek?.active ? 'Sí' : 'No'}</td>
                                        <td className={"text-center"}>
                                            <Button variant="link" size="sm" className="ms-2" to={`/configuration/day-of-the-week/${dayOfTheWeek.id}`} as={Link} disabled={!permissions.weekDays?.read}>
                                                <FontAwesomeIcon icon="edit" />
                                            </Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteDayOfTheWeek(dayOfTheWeek.id)} disabled={!permissions.weekDays?.delete}>
                                                <FontAwesomeIcon icon="trash" />
                                            </Button>
                                        </td>
                                    </tr>
                                )) :
                                    <tr>
                                        <td colSpan={4}>{t('general.empty')}</td>
                                    </tr>
                            }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                        setPagination={setPagination}
                        alwaysShown={daysOfTheWeeks.length > 0} />
                </Card.Body>
            </Card>
        </>
    )
};

export default DayOfTheWeek;